import { useIsCartInFlightRequests } from '@/features/pos/components/Cart/hooks/cart-optimistic-update-counter'
import { CartProductItem } from '@/features/pos/components/Cart/parts/Overview/parts/CartItem/types'
import { AutomaticContainerRemovals } from '@/features/pos/components/Cart/utils/automatic-containers'
import { useFormAutomaticContainerRemovalsField } from '@/features/pos/components/OrderFinalization/form'
import isEqual from 'lodash.isequal'
import { useEffect, useMemo } from 'react'

interface UseAutomaticContainerConfigurationOptions {
    items: CartProductItem[]
    enabled: boolean
    loaded: boolean
}

interface AutomaticContainerConfiguration {
    itemsWithFilteredAutomaticContainers: CartProductItem[]
    itemIdsWithAutomaticContainers: string[]
}

export const useAutomaticContainerConfiguration = ({
    items,
    enabled,
    loaded,
}: UseAutomaticContainerConfigurationOptions): AutomaticContainerConfiguration => {
    const [removals, setRemovals] = useFormAutomaticContainerRemovalsField()
    const isCartInFlightRequests = useIsCartInFlightRequests()
    const automaticContainerRemovals = useMemo(
        () => AutomaticContainerRemovals.from(removals),
        [removals]
    )

    const itemsWithFilteredAutomaticContainers = useMemo(() => {
        if (!enabled) {
            return items.map((item) =>
                item.containers?.length ? { ...item, containers: [] } : item
            )
        }

        return automaticContainerRemovals.applyTo(items)
    }, [automaticContainerRemovals, enabled, items])

    const itemIdsWithAutomaticContainers = useMemo(
        () =>
            enabled
                ? items.filter(({ containers }) => !!containers?.length).map(({ id }) => id)
                : [],
        [enabled, items]
    )

    useEffect(() => {
        if (!loaded || isCartInFlightRequests) return

        const reconciledRemovals = automaticContainerRemovals.reconcileWith(items).toArray()

        if (!isEqual(reconciledRemovals, removals)) {
            setRemovals(reconciledRemovals)
        }
    }, [automaticContainerRemovals, isCartInFlightRequests, items, loaded, removals, setRemovals])

    return { itemsWithFilteredAutomaticContainers, itemIdsWithAutomaticContainers }
}
